import { z } from "zod/v4";

/**
 * The vocabulary, matching the one the benchmark cohorts are keyed on.
 *
 * **One classifier, used on both sides of every comparison.** The funnel
 * insight places a store against a cohort of other stores, and a cohort
 * assembled by a different judge is the same failure as a cohort measured by a
 * different instrument — invisible in the output and fatal to the comparison.
 * So the benchmark builder and the live audit both classify through this same
 * list, and consistency is structural rather than maintained.
 *
 * All eighteen, not the twelve the benchmark currently publishes. Which labels
 * earn a cohort is a question for the data — the builder suppresses any that
 * come in under its store floor — and hard-coding today's twelve would stop a
 * label ever growing into one.
 *
 * It lives in types rather than beside the classifier because the answer
 * crosses a service boundary: web/ai decides the industry, node-backend reads
 * the benchmark row it selects.
 */
export const STORE_INDUSTRIES = [
  "Apparel & Accessories",
  "Supplements & Nutrition",
  "Beauty & Personal Care",
  "Home & Living",
  "Health & Wellness",
  "Food & Beverage",
  "Pet",
  "Arts, Crafts & Hobbies",
  "Electronics & Tech",
  "Outdoor & Sports",
  "Baby & Kids",
  "Miscellaneous",
  "B2B / Wholesale",
  "Education & Coaching",
  "Automotive",
  "Digital Media",
  "PC & Gaming",
  "Cannabis & CBD",
] as const;

export const StoreIndustrySchema = z.enum(STORE_INDUSTRIES);

/**
 * What the classifier is told about a store: its best sellers with their share
 * of sales, or its catalogue when it has sold nothing, plus what the store says
 * about itself and how Shopify files its products.
 */
export const StoreIndustryRequestSchema = z.object({
  products: z.array(
    z.object({
      title: z.string(),
      /** Share of sales, 0 to 1. Null for catalogue products. */
      share: z.number().nullable(),
    })
  ),
  source: z.enum(["sales", "catalog"]),
  /** The shop's own description, as search engines show it. */
  description: z.string().nullable(),
  /** Shopify taxonomy paths of those products, the largest first. */
  categories: z.array(z.string()),
});

/**
 * Null when there was too little to go on, or the answer is not one we offered:
 * a label outside the vocabulary has no cohort.
 *
 * `alternative` is set when two industries fit the store about equally well.
 * Cohorts compare on `industry` only.
 */
export const StoreIndustryClassificationSchema = z.object({
  industry: StoreIndustrySchema.nullable(),
  alternative: StoreIndustrySchema.nullable(),
});

export type StoreIndustry = z.infer<typeof StoreIndustrySchema>;
export type StoreIndustryRequestType = z.infer<
  typeof StoreIndustryRequestSchema
>;
export type StoreIndustryClassificationType = z.infer<
  typeof StoreIndustryClassificationSchema
>;
